import { useMemo, useState } from 'react'
import { enregistrerOffre, type DossierPme } from '../../api/onboarding'
import {
  apercuMensuel, LIBELLE_CLE_OFFRE, LIBELLE_MOYEN, lireOffre, MOYENS_PAIEMENT, offreDepuisFiche, offreManque,
  type CreneauOffre, type MoyenPaiement, type OffreChoisie, type PlanPme, type StatutOnboarding,
} from '../../api/onboardingRegles'
import { fcfa, JOURS } from '../Format'
import { Icone } from '../Icone'
import { ListeDeroulante, type OptionListe } from '../ListeDeroulante'
import { Bloc, Erreur, Info, useAction } from './Briques'

const OPTIONS_JOUR: OptionListe[] = JOURS.slice(1).map((j, i) => ({ valeur: String(i + 1), libelle: j }))

/**
 * Offre choisie à l'onboarding : forfait (catalogue `managed_plans`), option longue distance, créneaux,
 * supplément hors créneau, conditions de paiement, prix négocié si « sur devis ». Elle alimente le contrat
 * (variables du PDF) puis, à l'activation, la fiche client (forfait, option, supplément, créneaux — c'est
 * cette fiche que lit le prix des courses). Figée dès que le contrat est parti.
 */
export function SectionOffre({
  dossier,
  plans,
  grille,
  statut,
  modifiable,
  surChange,
}: {
  dossier: DossierPme
  plans: PlanPme[]
  grille: { option_longue_distance_mensuel_fcfa: number } | null
  statut: StatutOnboarding
  modifiable: boolean
  surChange: () => void
}) {
  const repli = useMemo(() => offreDepuisFiche(dossier, plans), [dossier, plans])
  const [o, setO] = useState<OffreChoisie>(() => lireOffre(dossier.offre_choisie, repli))
  const [prixTexte, setPrixTexte] = useState(typeof o.prix_negocie_fcfa === 'number' ? String(o.prix_negocie_fcfa) : '')
  const { occupe, erreur, info, lancer } = useAction()

  const fige = statut === 'contrat_envoye' || statut === 'contrat_signe' || statut === 'actif'
  const ro = !modifiable || fige
  const prixNegocie = prixTexte.trim() === '' ? undefined : Number(prixTexte.replace(/\s/g, '').replace(',', '.'))
  const courante: OffreChoisie = { ...o, ...(prixNegocie !== undefined ? { prix_negocie_fcfa: prixNegocie } : {}) }
  if (prixNegocie === undefined) delete courante.prix_negocie_fcfa
  const plan = plans.find((p) => p.code === o.plan_code)
  const manque = offreManque(courante, plans)
  const apercu = apercuMensuel(courante, plan, grille)

  const majCreneau = (i: number, patch: Partial<CreneauOffre>) =>
    setO((x) => ({ ...x, creneaux: x.creneaux.map((c, k) => (k === i ? { ...c, ...patch } : c)) }))
  const basculeMoyen = (m: MoyenPaiement) =>
    setO((x) => {
      const a = x.conditions_paiement.moyens
      return { ...x, conditions_paiement: { ...x.conditions_paiement, moyens: a.includes(m) ? a.filter((y) => y !== m) : [...a, m] } }
    })

  const optionsPlan: OptionListe[] = plans.map((p) => ({
    valeur: p.code,
    libelle: `${p.name} — ${p.price_fcfa === null ? 'sur devis' : `${fcfa(p.price_fcfa)}/mois`}${p.monthly_courses === null ? '' : ` · ${p.monthly_courses} courses`}`,
  }))

  function enregistrer() {
    void lancer(async () => {
      await enregistrerOffre(dossier.id, courante)
      surChange()
      return manque.length ? 'Brouillon d’offre enregistré (encore incomplet).' : 'Offre enregistrée.'
    })
  }

  return (
    <Bloc titre="Offre choisie à l’onboarding" lecture={ro}>
      <div className="field">
        <label>Forfait</label>
        <ListeDeroulante valeur={o.plan_code} options={optionsPlan} desactive={ro} pleineLargeur surChangement={(v) => setO({ ...o, plan_code: v })} />
      </div>
      {plan && plan.price_fcfa === null && (
        <p className="onb-note">Forfait « sur devis » : le prix négocié est obligatoire.</p>
      )}
      <div className="onb-champs">
        <div className="field">
          <label>Prix mensuel négocié (F){plan && plan.price_fcfa !== null ? ' — facultatif' : ' *'}</label>
          <input inputMode="numeric" value={prixTexte} disabled={ro} placeholder={plan?.price_fcfa != null ? `catalogue : ${fcfa(plan.price_fcfa)}` : 'ex. 90000'} onChange={(e) => setPrixTexte(e.target.value)} />
        </div>
        <div className="field">
          <label>Supplément hors créneau (F par course)</label>
          <input type="number" min={0} step={250} value={o.supplement_hors_creneau_fcfa} disabled={ro}
            onChange={(e) => setO({ ...o, supplement_hors_creneau_fcfa: Math.max(0, Math.trunc(Number(e.target.value) || 0)) })} />
        </div>
      </div>
      <label className="onb-coche" style={{ margin: '4px 0 10px' }}>
        <input type="checkbox" checked={o.option_longue_distance} disabled={ro} onChange={(e) => setO({ ...o, option_longue_distance: e.target.checked })} />
        Option « longue distance »{grille ? ` (+ ${fcfa(grille.option_longue_distance_mensuel_fcfa)}/mois)` : ''}
      </label>

      <div className="onb-mensuel" aria-live="polite">
        {apercu ? (
          <>
            <span className="total">{fcfa(apercu.total)}<span style={{ fontSize: 13, fontWeight: 700 }}> /mois</span></span>
            <span className="det">
              Forfait {fcfa(apercu.forfait)}
              {apercu.option > 0 ? ` + option longue distance ${fcfa(apercu.option)}` : ''}
              <br />
              {apercu.quota === null ? 'Volume convenu (forfait sur devis)' : `${apercu.quota} courses incluses ; au-delà, tarif de la grille à la course`}
            </span>
          </>
        ) : (
          <span className="det">Aperçu mensuel indisponible : forfait sans prix au catalogue — saisissez le prix négocié.</span>
        )}
      </div>

      <div className="field">
        <label>Créneaux proposés (livreur auto-assigné dedans)</label>
        {o.creneaux.map((c, i) => (
          <div className="onb-creneau" key={i}>
            <ListeDeroulante valeur={String(c.weekday)} options={OPTIONS_JOUR} desactive={ro} surChangement={(v) => majCreneau(i, { weekday: parseInt(v, 10) })} />
            <input type="time" value={c.start_time} disabled={ro} onChange={(e) => majCreneau(i, { start_time: e.target.value })} />
            <input type="time" value={c.end_time} disabled={ro} onChange={(e) => majCreneau(i, { end_time: e.target.value })} />
            <input className="lib" placeholder="Libellé (facultatif)" value={c.label ?? ''} disabled={ro} onChange={(e) => majCreneau(i, { label: e.target.value })} />
            {!ro && (
              <button className="btn btn-ghost btn-sm" type="button" title="Retirer ce créneau" aria-label="Retirer ce créneau"
                onClick={() => setO((x) => ({ ...x, creneaux: x.creneaux.filter((_, k) => k !== i) }))}>
                <Icone nom="x" />
              </button>
            )}
          </div>
        ))}
        {!ro && (
          <button className="btn btn-ghost btn-sm" type="button"
            onClick={() => setO((x) => ({ ...x, creneaux: [...x.creneaux, { weekday: 1, start_time: '09:00', end_time: '11:00' }] }))}>
            <Icone nom="plus" /> Ajouter un créneau
          </button>
        )}
      </div>

      <div className="field">
        <label>Conditions de paiement</label>
        <div className="onb-moyens">
          {MOYENS_PAIEMENT.map((m) => (
            <label key={m}>
              <input type="checkbox" checked={o.conditions_paiement.moyens.includes(m)} disabled={ro} onChange={() => basculeMoyen(m)} />
              {LIBELLE_MOYEN[m]}
            </label>
          ))}
        </div>
        <div className="onb-champs">
          <div className="field">
            <label>Échéance de la facture mensuelle (jours)</label>
            <input type="number" min={0} max={120} value={o.conditions_paiement.echeance_jours} disabled={ro}
              onChange={(e) => setO({ ...o, conditions_paiement: { ...o.conditions_paiement, echeance_jours: Math.trunc(Number(e.target.value) || 0) } })} />
          </div>
        </div>
      </div>

      {fige && <p className="onb-note">L’offre est figée depuis l’envoi du contrat. Pour la modifier : retirer le contrat (section Décision).</p>}
      {!fige && manque.length > 0 && (
        <div className="onb-alerte">Offre incomplète — il manque : {manque.map((k) => LIBELLE_CLE_OFFRE[k] ?? k).join(' ; ')}.</div>
      )}
      {!ro && (
        <div className="onb-actions">
          <button className="btn btn-gold btn-sm" disabled={occupe} onClick={enregistrer}>{occupe ? 'Enregistrement…' : 'Enregistrer l’offre'}</button>
        </div>
      )}
      <Erreur texte={erreur} />
      <Info texte={info} />
    </Bloc>
  )
}
